import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UserDialogComponent } from '../../user/user-dialog.component';
import { getUserRoleName, ONBOARDING_STEPS } from '../../common/user';
import { User } from '../../common/user';
import { UserService } from '../user.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../ui-components/dialogs/confirm-dialog.component';
import { DownloadService } from '../../model-services/download.service';
import { Column } from '../../ui-components/table/table-component';

const DAY = 24 * 60 * 60 * 1000

// the onboarding steps a user has completed, in words rather than their numbers
const STEP_NAMES:{ [step in ONBOARDING_STEPS]: string } = {
    [ONBOARDING_STEPS.WALKTHROUGH]: 'Walkthrough',
    [ONBOARDING_STEPS.TUTORIAL]: 'Tutorial',
    [ONBOARDING_STEPS.FIRST_MODEL]: 'First model',
    [ONBOARDING_STEPS.HOME_INTRO_SHOWN]: 'Home introduction',
    [ONBOARDING_STEPS.HELP_VIDEOS]: 'Help videos',
    [ONBOARDING_STEPS.FIRST_MODEL_SAVE]: 'Model saved',
    [ONBOARDING_STEPS.FIRST_INSIGHTS]: 'Insights page',
    [ONBOARDING_STEPS.CHECKLIST_HIDDEN]: 'Checklist hidden',
}

@Component({
    selector: 'app-users',
    templateUrl: './users-admin.html',
    styleUrls: ['./users-admin.css'],
    standalone: false
})
export class UsersAdminComponent implements OnChanges {
    @Input() users:User[]
    @Input() loading = false

    columns:Column[]
    data:any[]
    summary:{ label:string, value:number, hint:string }[] = []

    constructor(public dialog: MatDialog, private userService: UserService, private downloadService:DownloadService) {
        this.columns = [
            new Column('name', 'Name', 'string'),
            new Column('email', 'Email', 'string'),
            new Column('createdDate', 'Created', 'date'),
            new Column('signInDate', 'Last login', 'date'),
            new Column('organisations', 'Organisations', 'string'),
            new Column('utm', 'Source', 'string'),
            new Column('roleName', 'Role', 'string'),
            new Column('activatedText', 'Activated', 'string'),
            new Column('steps', 'Onboarding', 'string'),
            new Column('canEdit', 'Update', 'icon-button', 'Update user details', 'accent', 'edit'),
            new Column('canDelete', 'Delete', 'icon-button', 'Delete this user', 'warn', 'delete')
        ]
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['users'] && this.users) this.refresh()
    }

    // the table shows display copies of the users; the users themselves are what gets edited and saved
    private refresh() {
        this.data = this.users
            .map((u:any) => ({
                _key: u._key,
                name: u.name,
                email: u.email,
                createdDate: u.createdDate,
                signInDate: u.signInDate,
                organisations: this.getOrganisationsValue(u.organisations),
                utm: u.utm?.source || '',
                roleName: getUserRoleName(u.role),
                activatedText: u.activated ? 'Yes' : 'No',
                steps: this.getSteps(u),
                canEdit: true,
                canDelete: true,
            }))
            // newest first
            .sort((a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime())

        const now = Date.now()
        const signedInWithin = (days:number) => this.users.filter(u => u.signInDate && now - new Date(u.signInDate).getTime() <= days * DAY).length
        this.summary = [
            { label: 'Users', value: this.users.length, hint: 'All LINQ user accounts' },
            { label: 'Activated', value: this.users.filter((u:any) => u.activated).length, hint: 'Users who have activated their account' },
            { label: 'Active in 30 days', value: signedInWithin(30), hint: 'Users who logged in within the last 30 days' },
            { label: 'Joined in 30 days', value: this.users.filter(u => u.createdDate && now - new Date(u.createdDate).getTime() <= 30 * DAY).length, hint: 'Accounts created within the last 30 days' },
        ]
    }

    getTableHeight() {
        return Math.max(300, window.innerHeight - 330)
    }

    async handleRowEvent(rowEvent:{row:any, column?:Column}) {
        const user = rowEvent.row && this.users.find(u => u._key === rowEvent.row._key)
        if (!user || !rowEvent.column) return
        if (rowEvent.column.name === "canDelete") this.deleteUser(user)
        else if (rowEvent.column.name === "canEdit") this.updateUser(user)
    }

    public updateUser(user:User) {
        const dialogRef = this.dialog.open(UserDialogComponent, {data:{user, isInvite:false}, width:'520px', maxWidth:'95vw', hasBackdrop:true});

        dialogRef.afterClosed().subscribe(async result => {
            if (result) {
                await this.userService.saveUser(result)
                this.users = this.users.map(u => u._key === result._key ? result : u)
                this.refresh()
            }
        })
    }

    public async deleteUser(user:User) {
        const message = `Are you sure you want to permanently delete ${user.name} (${user.email})? This can't be undone.`;
        const dialogData = new ConfirmDialogModel("Delete user", [message], "delete");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "500px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                await this.userService.deleteUser(user)
                this.users = this.users.filter(u => u._key !== user._key)
                this.refresh()
            }
        });
    }

    async exportUsers() {
        this.downloadService.createCSVUserDownload(this.users)
    }

    getOrganisationsValue(orgs:string[]) {
        return orgs?orgs.join(', '):""
    }

    getSteps(user:User) {
        const steps = user.onboardingSteps || []
        return steps.length ? steps.map(s => STEP_NAMES[s] ?? s).join(', ') : 'None'
    }
}
